import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft, Rss, Plus, Pencil, Trash2, ExternalLink,
    AlertCircle, Facebook, RefreshCw, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
    listRssFeeds, createRssFeed, updateRssFeed, deleteRssFeed, pollFeedNow,
    listPagesConfig, listFeedItems,
    type SocialRssFeed, type SocialPageConfig,
} from '@/services/api/social-publisher';
import { fmtRelative } from './dates';


function FeedForm({
    open, onOpenChange, editing, configs, onSaved,
}: {
    open: boolean; onOpenChange: (b: boolean) => void;
    editing: SocialRssFeed | null;
    configs: SocialPageConfig[];
    onSaved: () => void;
}) {
    const { toast } = useToast();
    const [url, setUrl] = useState('');
    const [name, setName] = useState('');
    const [ctaOverride, setCtaOverride] = useState('');
    const [customPrompt, setCustomPrompt] = useState('');
    const [linked, setLinked] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setUrl(editing.feed_url);
            setName(editing.name || '');
            setCtaOverride(editing.cta_url_override || '');
            setCustomPrompt(editing.custom_prompt || '');
            setLinked(new Set(editing.page_config_ids || []));
        } else {
            setUrl(''); setName(''); setCtaOverride(''); setCustomPrompt('');
            setLinked(new Set());
        }
    }, [open, editing]);

    const toggle = (id: number) => {
        setLinked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const mut = useMutation({
        mutationFn: async () => {
            const ids = Array.from(linked);
            const override = ctaOverride.trim() || null;
            const prompt = customPrompt.trim() || null;
            if (editing) {
                return updateRssFeed(editing.id, {
                    feed_url: url,
                    name: name || undefined,
                    cta_url_override: override,
                    custom_prompt: prompt,
                    page_config_ids: ids,
                });
            }
            return createRssFeed({
                feed_url: url,
                name: name || undefined,
                cta_url_override: override,
                custom_prompt: prompt,
                page_config_ids: ids,
            });
        },
        onSuccess: () => { toast({ title: editing ? 'Atualizado' : 'Criado' }); onSaved(); },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] p-0 border-none rounded-2xl overflow-hidden">
                <div className="bg-white">
                    <div className="px-5 py-3 border-b">
                        <h3 className="text-base font-semibold">
                            {editing ? 'Editar feed' : 'Novo feed RSS'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Vincule a uma ou mais páginas — cada página gera conteúdo único
                        </p>
                    </div>
                    <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                        <div>
                            <Label className="text-xs text-gray-500">URL do feed</Label>
                            <Input
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://seublog.com/feed.xml"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Nome (opcional)</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Blog principal"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">
                                Link CTA fixo (opcional)
                            </Label>
                            <Input
                                value={ctaOverride}
                                onChange={(e) => setCtaOverride(e.target.value)}
                                placeholder="https://app.jikiviral.xyz/toplinks-jikiviral-a"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                Se preenchido, <strong>todos os posts</strong> deste feed apontam pra esta URL
                                em vez do link original do artigo. Útil pra hub/linkinbio (JikiViral, etc).
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">
                                Instruções pra IA (opcional)
                            </Label>
                            <Textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                placeholder="Ex: Foque em consumidor brasileiro. Sempre termine com pergunta provocativa sobre o futuro dos carros. Use linguagem descontraída, evite jargão técnico."
                                rows={4}
                                className="text-sm"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                Instruções extras passadas pra IA na hora de gerar o texto (post + story).
                                Aplicado a TODOS os posts deste feed.
                            </p>
                        </div>
                        <div className="pt-3 border-t">
                            <Label className="text-xs text-gray-500 mb-2 block">
                                Páginas que vão usar este feed
                            </Label>
                            {configs.length === 0 ? (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>
                                        Nenhuma página configurada ainda. Crie uma em{' '}
                                        <Link to="/meta/social-publisher" className="underline">
                                            Social Publisher
                                        </Link>.
                                    </span>
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                    {configs.map((c) => (
                                        <label
                                            key={c.id}
                                            className="flex items-center gap-3 px-3 py-2 rounded-lg border hover:bg-gray-50 cursor-pointer"
                                        >
                                            <Checkbox
                                                checked={linked.has(c.id)}
                                                onCheckedChange={() => toggle(c.id)}
                                            />
                                            {c.page_picture_url ? (
                                                <img
                                                    src={c.page_picture_url}
                                                    className="w-8 h-8 rounded-full object-cover"
                                                    alt=""
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                                                    <Facebook className="w-4 h-4" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-800 truncate">
                                                    {c.page_name || `page_pk #${c.page_pk}`}
                                                </div>
                                                {c.persona_name && (
                                                    <div className="text-[11px] text-purple-600 truncate">
                                                        🎭 {c.persona_name}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="px-5 py-3 border-t bg-gray-50/50 flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => mut.mutate()}
                            disabled={!url.startsWith('http') || mut.isPending}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {mut.isPending ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function FeedItemsPreview({ feedId }: { feedId: number }) {
    const { data: items = [], refetch, isFetching } = useQuery({
        queryKey: ['social-publisher', 'feed-items', feedId],
        queryFn: () => listFeedItems(feedId, 20),
    });
    return (
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-gray-500 uppercase tracking-wider">
                    Últimos items
                </span>
                <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                    recarregar
                </Button>
            </div>
            {items.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-3">
                    Nenhum item ainda — beat puxa a cada 15min
                </div>
            ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                    {items.map((it) => (
                        <div key={it.id} className="text-xs px-2 py-1 rounded hover:bg-white/60">
                            <div className="font-medium text-gray-700 truncate">{it.title || it.guid}</div>
                            <div className="text-[10px] text-gray-400">
                                {it.published_at ? fmtRelative(it.published_at) : '—'} · {it.status}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


export default function FeedsPage() {
    const qc = useQueryClient();
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<SocialRssFeed | null>(null);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const { data: feeds = [], isLoading } = useQuery({
        queryKey: ['social-publisher', 'rss-feeds'],
        queryFn: listRssFeeds,
        refetchInterval: 1000 * 60,
    });
    const { data: configs = [] } = useQuery({
        queryKey: ['social-publisher', 'pages-config'],
        queryFn: () => listPagesConfig(),
    });

    const del = useMutation({
        mutationFn: (id: number) => deleteRssFeed(id),
        onSuccess: () => {
            toast({ title: 'Feed removido' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'rss-feeds'] });
        },
    });

    const poll = useMutation({
        mutationFn: (id: number) => pollFeedNow(id),
        onSuccess: () => {
            toast({
                title: 'Pull enfileirado',
                description: 'Os items vão aparecer em alguns segundos. Recarregue pra ver.',
            });
            setTimeout(() => qc.invalidateQueries({ queryKey: ['social-publisher', 'rss-feeds'] }), 4000);
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/meta/social-publisher" className="text-gray-400 hover:text-gray-600">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <Rss className="w-5 h-5 text-purple-600" /> Feeds RSS
                        </h1>
                        <p className="text-xs text-gray-500">
                            Catálogo global — cada feed pode alimentar várias páginas
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => { setEditing(null); setOpen(true); }}
                    className="bg-purple-600 hover:bg-purple-700"
                >
                    <Plus className="w-4 h-4 mr-2" /> Novo feed
                </Button>
            </div>

            {isLoading ? (
                <div className="text-center text-gray-400 py-12">Carregando...</div>
            ) : feeds.length === 0 ? (
                <div className="bg-white border rounded-xl p-12 text-center">
                    <Rss className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500 mb-4">Nenhum feed cadastrado</p>
                    <Button onClick={() => { setEditing(null); setOpen(true); }} size="sm">
                        <Plus className="w-4 h-4 mr-2" /> Adicionar primeiro feed
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {feeds.map((f) => {
                        const linkedConfigs = configs.filter((c) => (f.page_config_ids || []).includes(c.id));
                        const isExp = expanded.has(f.id);
                        return (
                            <div key={f.id} className="bg-white rounded-xl border overflow-hidden">
                                <div className="p-4 flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-gray-800 truncate">
                                            {f.name || f.feed_url}
                                        </div>
                                        <a
                                            href={f.feed_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[11px] text-purple-600 hover:underline inline-flex items-center gap-0.5"
                                        >
                                            {f.feed_url} <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                        {f.cta_url_override && (
                                            <div className="text-[11px] text-emerald-700 mt-0.5 truncate">
                                                <span className="font-medium">CTA fixo:</span>{' '}
                                                <a
                                                    href={f.cta_url_override}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="hover:underline"
                                                >
                                                    {f.cta_url_override}
                                                </a>
                                            </div>
                                        )}
                                        {f.custom_prompt && (
                                            <div
                                                className="text-[11px] text-indigo-700 mt-0.5 truncate"
                                                title={f.custom_prompt}
                                            >
                                                <span className="font-medium">🧠 Prompt:</span>{' '}
                                                {f.custom_prompt.length > 80
                                                    ? f.custom_prompt.slice(0, 80) + '…'
                                                    : f.custom_prompt}
                                            </div>
                                        )}
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {linkedConfigs.length === 0 ? (
                                                <span className="text-[11px] text-amber-600">
                                                    Não vinculado a nenhuma página
                                                </span>
                                            ) : (
                                                linkedConfigs.map((c) => (
                                                    <span
                                                        key={c.id}
                                                        className="text-[11px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1"
                                                    >
                                                        {c.page_picture_url && (
                                                            <img src={c.page_picture_url} className="w-3.5 h-3.5 rounded-full" alt="" />
                                                        )}
                                                        {c.page_name || `#${c.page_pk}`}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                        <div className="mt-2 text-[10px] text-gray-400 flex items-center gap-3">
                                            <span>
                                                {f.last_polled_at
                                                    ? `Puxado ${fmtRelative(f.last_polled_at)}`
                                                    : 'Aguardando primeiro pull'}
                                            </span>
                                            {f.last_error && (
                                                <span className="text-red-600 inline-flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" /> {f.last_error}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            size="sm" variant="outline"
                                            onClick={() => poll.mutate(f.id)}
                                            disabled={poll.isPending}
                                            className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                            title="Forçar pull do RSS agora"
                                        >
                                            <Download className={`w-3.5 h-3.5 mr-1 ${poll.isPending ? 'animate-pulse' : ''}`} />
                                            Pull agora
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => {
                                                const next = new Set(expanded);
                                                if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                                                setExpanded(next);
                                            }}
                                        >
                                            {isExp ? 'Fechar' : 'Itens'}
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => { setEditing(f); setOpen(true); }}>
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => { if (confirm('Remover feed?')) del.mutate(f.id); }}
                                            className="text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                {isExp && <FeedItemsPreview feedId={f.id} />}
                            </div>
                        );
                    })}
                </div>
            )}

            <FeedForm
                open={open}
                onOpenChange={setOpen}
                editing={editing}
                configs={configs}
                onSaved={() => {
                    setOpen(false);
                    qc.invalidateQueries({ queryKey: ['social-publisher', 'rss-feeds'] });
                }}
            />
        </div>
    );
}
